/// <amd-module name="TFS/NetPromoterSurvey/NetPromoterService" />

import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { traceError } from "VSS/Platform/Trace";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";

import { AnonymousRecommender } from "TFS/NetPromoterSurvey/Recommenders/AnonymousRecommender";
import { IDecision, IRecommender, Recommender } from "TFS/NetPromoterSurvey/Recommenders/Recommender";
import * as NetPromoterSurveyResources from "TFS/NetPromoterSurvey/Resources";
import { SurveyComponent } from "TFS/NetPromoterSurvey/SurveyComponent";

interface INetPromoterService extends IVssService {
    initialize(): void;
}

class NetPromoterService extends VssService implements INetPromoterService {
    private _recommender: IRecommender;

    public async initialize() {
        const isAuthenticated = this._isAuthenticated();
        const hub = this._getSelectedHub();

        let decision: IDecision;
        try {
            decision = await this._recommend(isAuthenticated, this._hubGroupToProductName(hub[0]));
        } catch (error) {
            traceError(this.pageContext, "Engagement", "NetPromoterSurvey", format("Recommend Failed: {0}", error));
            decision = { isPositive: false };
        }

        if (decision.isPositive) {
            this._recommender.logRecommendation();

            const useNewBranding = isFeatureFlagEnabled(this.pageContext, "VisualStudio.Services.WebPlatform.UseNewBranding", false);
            const NPSVerticalBrandingEnabled = isFeatureFlagEnabled(this.pageContext, "WebAccess.Survey.NetPromoter.VerticalBranding", false);
            const verticalBrandingEnabled = NPSVerticalBrandingEnabled;

            const layoutService = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            if (!document.getElementById("nps-dialog")) {
                layoutService.renderCallout((onDismiss: () => void) => {
                    return (
                        React.createElement(SurveyComponent, {
                            recommender: this._recommender,
                            isAuthenticated: isAuthenticated,
                            verticalNavigationEnabled: true,
                            verticalBrandingEnabled: NPSVerticalBrandingEnabled,
                            useNewBranding: useNewBranding,
                            productName: this._buildDisplayedProductName(hub[0], useNewBranding, verticalBrandingEnabled),
                            selectedHubGroupId: hub[0],
                            selectedHubId: hub[1],
                            data: decision.data || {},
                            onDismiss: onDismiss
                        }) || {}
                    );
                });
            }
        }
    }

    /**
     * Returns a NPS prompt decision for the current user
     * @param isAuthenticated True if the current user is authenticated
     */
    private async _recommend(isAuthenticated: boolean, productName: string): Promise<IDecision> {
        if (isAuthenticated) {
            this._recommender = new Recommender(this.pageContext, productName);
        } else {
            this._recommender = new AnonymousRecommender(this.pageContext);
        }
        return this._recommender.recommmend();
    }

    /**
     * Returns true if the current user is authenticated
     */
    private _isAuthenticated(): boolean {
        return !userHasClaim(this.pageContext, UserClaims.Anonymous);
    }

    /**
     * Returns an array containing the currently selected hub group (index 0)
     * and hub (index 1)
     */
    private _getSelectedHub(): string[] {
        const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const displayedNavigation = navigationService.getDisplayedNavigation();
        const selectedHubGroupId = (displayedNavigation.length > 2 && displayedNavigation[2]) || "N/A";
        const selectedHubId = (displayedNavigation.length > 3 && displayedNavigation[3]) || "N/A";
        return [selectedHubGroupId, selectedHubId];
    }

    /**
     * Builds the product name for various feature availability
     * Used a hub group id->name mapping instead of contribution service
     * to support anonymous access
     */
    private _buildDisplayedProductName = (selectedHubGroupId: string, useNewBranding: boolean, verticalBranding: boolean): string => {
        if (!useNewBranding) {
            return NetPromoterSurveyResources.VSTSBrandName;
        }

        const brandName = !verticalBranding ? NetPromoterSurveyResources.SuiteName : this._hubGroupToProductName(selectedHubGroupId);
        return format(NetPromoterSurveyResources.BrandNameTemplate, brandName);
    };

    private _hubGroupToProductName(id: string): string {
        switch (id) {
            case "ms.vss-work-web.work-hub-group":
                return "Boards";
            case "ms.vss-code-web.code-hub-group":
                return "Repos";
            case "ms.vss-build-web.build-release-hub-group":
                return "Pipelines";
            case "ms.feed.package-hub-group":
                return "Artifacts";
            case "ms.vss-test-web.test-hub-group":
                return "Test Plans";
            default:
                return NetPromoterSurveyResources.SuiteName;
        }
    }
}

Services.add("INetPromoterService", { serviceFactory: NetPromoterService });
